<script setup>

import {ref, watch} from 'vue';

const props = defineProps({
    courseId: {
        type: Number,
        required: true
    }
});

const course = ref(null);
const courseLoaded = ref(false);
const currentPage = ref(1);
const lastPage = ref(1);
const coursePageSize = 10;

const initialPageFromUrl = () => {
    const parsed = Number.parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);

    if (Number.isNaN(parsed)) {
        return 1;
    }

    return Math.max(1, parsed);
};

const normalizedPerPage = () => Math.max(1, Math.min(50, coursePageSize));

const coursePageUrl = (page) => {
    const courseUrl = route('courses.feed.customerarea.show', props.courseId);

    if (page <= 1) {
        return courseUrl;
    }

    return `${courseUrl}?page=${page}`;
};

const syncBrowserUrl = (page) => {
    window.history.replaceState({}, document.title, coursePageUrl(page));
};

const loadCourse = async (page = currentPage.value) => {
    courseLoaded.value = false;

    try {
        const res = await axios.get(route('api.courses.feed.customerarea.show', props.courseId), {
            params: {
                page,
                per_page: normalizedPerPage(),
            }
        });

        if (res.data.success) {
            course.value = res.data.course;
            currentPage.value = res.data.course?.pagination?.current_page ?? page;
            lastPage.value = res.data.course?.pagination?.last_page ?? 1;
            syncBrowserUrl(currentPage.value);
        } else {
            course.value = null;
            currentPage.value = 1;
            lastPage.value = 1;
            syncBrowserUrl(1);
        }
    } catch (error) {
        course.value = null;
        currentPage.value = 1;
        lastPage.value = 1;
        syncBrowserUrl(1);
    }

    courseLoaded.value = true;
};

const changePage = (page) => {
    if (page < 1 || page > lastPage.value || page === currentPage.value) {
        return;
    }

    loadCourse(page);
};

watch(() => props.courseId, () => {
    currentPage.value = initialPageFromUrl();
    loadCourse(currentPage.value);
}, { immediate: true });

</script>

<template>
    <div
        v-if="course"
        class="flex items-stretch min-h-screen"
        :class="'course-id-' + courseId"
    >
        <div class="w-full mx-auto mt-4 max-w-3xl">
            <h1>{{ course.title }}</h1>
            <div
                v-if="course.description"
                v-html="course.description"
            ></div>

            <ul class="space-y-4 mt-8">
                <li
                    v-for="chapter in course.chapters"
                    :key="chapter.id"
                >
                    <card-basic margin-bottom-class="mb-0">
                        <a
                            :href="route('courses.feed.customerarea.chapter.show', [course.id, chapter.id])"
                            class="text-inherit mf-link-immune"
                        >
                            <h2 class="text-lg font-semibold">{{ chapter.title }}</h2>
                        </a>
                        <div class="text-xs text-gray-500">
                            {{ _mft('course:blog.publishedOn') }} {{ chapter.published_at_user_tz_display }}
                        </div>
                        <p
                            v-if="chapter.description"
                            class="mt-2 whitespace-pre-wrap text-sm text-gray-700 !mb-0"
                        >
                            {{ chapter.description }}
                        </p>
                        <div class="flex justify-end">
                            <a
                                :href="route('courses.feed.customerarea.chapter.show', [course.id, chapter.id])"
                                class="text-sm mf-link-immune"
                            >
                                <span class="inline-block mt-3 mf-link">{{ _mft('course:blog.readChapter') }}</span>
                            </a>
                        </div>
                    </card-basic>
                </li>
            </ul>

            <div
                v-if="courseLoaded && course.chapters.length === 0"
                class="mt-8 text-gray-500"
            >
                {{ _mft('course:blog.noChapters') }}
            </div>

            <div
                v-if="lastPage > 1"
                class="flex items-center justify-center space-x-4 mt-8"
            >
                <button
                    class="mf-link-immune text-sm"
                    @click.prevent="changePage(currentPage - 1)"
                >
                    <i class="fa-regular fa-arrow-left"></i>
                </button>

                <span class="text-sm text-gray-500">
                    {{ _mft('course:blog.pageLabel', { current: currentPage, total: lastPage }) }}
                </span>

                <button
                    class="mf-link-immune text-sm"
                    @click.prevent="changePage(currentPage + 1)"
                >
                    <i class="fa-regular fa-arrow-right"></i>
                </button>
            </div>
        </div>
    </div>
    <div
        v-else-if="!courseLoaded"
        class="flex justify-center mt-24"
    >
        <mf-spinner-medium class="w-24 h-12" />
    </div>
</template>
